import { Injectable, inject } from '@angular/core';
import { ISectionCardData } from '@fi-shared/fi-section-card/fi-section-card';
import { AppTextService } from '@fi-shared/app-text';
import { FiDateFormatOptions, FiDateTimeFormatOptions, isEqual } from '@fi-shared/utils';
import {
    AccessRightFilterCollectionModel,
    IAccessRightFilterCollection,
} from '@fi-shared/models';
import { ObjectTypes, SideListOptionType } from '../types';
import {
    ChecklistCreationTypes,
    Checkpoint2StatusType,
    CheckpointConditionTargetType,
    CheckpointConditionType,
} from './checklist-enum';
import { checklistEditDefaultSections } from './fi-checklist-sections-data';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { FiChecklistStoreService } from '@fi-shared/checklist/fi-checklist-store.service';
import {
    IChecklistScheduleForList,
    IChecklistV2,
    IChecklistV2ForList,
    IChecklistV2ViewerBase,
    ICheckpoint2AnswerModel,
    ICheckpointCondition,
    ICheckpointGroup,
    ICheckpointRange,
} from '@fi-shared/checklist/checklist-v2';
import { INameIdObj, TResultPromise, TResultPromiseData } from '@fi-shared/fi-shared-interfaces';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiChecklistCommonDataDialogComponent } from '@fi-shared/checklist/fi-checklist2-edit/checklist-common-data-dialog/checklist-common-data-dialog.component';
import { IDialogConfirmDeleteData } from '@fi-shared/fi-dialogs-generic/confirm-delete/dialog-confirm-delete-data';
import { IFiDialogOptions, IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import { FiDialogConfirmDeleteComponent } from '@fi-shared/fi-dialogs-generic/confirm-delete/dialog-confirm-delete.component';
import { FiChecklistScheduleType } from '@fi-shared/checklist/schedule/schedule-edit/fi-checklist-schedule-type';
import { PublicationItemStatus } from '@fi-shared/fi-edit-publication/publication-item-status';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { ChecklistViewerStatus } from '@fi-shared/checklist/fi-checklist-viewer-status';
import { IAddChecklistDialogResult, IAddChecklistTemplateDialogResult } from '@fi-shared/checklist/checklist';
import { FiChecklistAddChecklistDialogComponent } from '@fi-shared/checklist/fi-checklist2-edit/add-checklist-dialog/add-checklist-dialog.component';
import { FiChecklist2TemplateService } from '@fi-shared/checklist/fi-checklist2-template/fi-checklist2-template.service';
import { fiTextToHtml } from '@fi-shared/utils/fi-text-to-html';
import { FiCheckpoint2HelperService } from '@fi-shared/checklist/fi-checkpoint2-helper.service';
import { RawParams } from '@uirouter/angular';
import { isNullOrUndefined } from '@fi-shared/utils/is-null-or-undefined';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiChecklist2ScheduleService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule.service';
import { FiChecklistAddChecklistTemplateDialogComponent } from '@fi-shared/checklist/fi-checklist2-template/add-checklist-template-dialog/add-checklist-template-dialog.component';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { IAIChecklist2Model, IAICheckpoint2Model } from '@fi-shared/fi-ai-assistance/fi-ai-assistance';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { FiChecklist2ScheduleFormatService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-format.service';

export interface IChecklistCheckpointPlaceholderLabelData {
    namePlaceholderTextId: string;
    namePlaceholderHintTextId: string;
    descriptionPlaceholderHintTextId?: string;
}

@Injectable()
export class FiChecklistHelperService {
    private appTextService = inject(AppTextService);
    private fiContentLanguageSharedService = inject(FiContentLanguageSharedService);
    private fiChecklistStoreService = inject(FiChecklistStoreService);
    private fiDialogsService = inject(FiDialogsService);
    private fiStateService = inject(FiStateService);
    private fiFormatDateService = inject(FiFormatDateService);
    private fiSnackbarService = inject(FiSnackBarService);
    private fiCheckpoint2HelperService = inject(FiCheckpoint2HelperService);
    private fiChecklist2TemplateService = inject(FiChecklist2TemplateService);
    private fiChecklist2ScheduleService = inject(FiChecklist2ScheduleService);
    private saveChangesWarningService = inject(SaveChangesWarningService);
    private fiCustomerDataService = inject(FiCustomerDataService);
    private fiItemsDeleteHelperService = inject(FiItemsDeleteHelperService);
    private fiChecklist2ScheduleFormatService = inject(FiChecklist2ScheduleFormatService);

    private readonly auditNotStartedUnitId = -1;

    isAIAssistanceEnabled(): boolean {
        return true;
        //return this.fiCustomerDataService.isAIAssistanceDevCustomer();
    }

    getCheckpointGroupInstanceV2(): ICheckpointGroup {
        return {
            Names: this.fiCheckpoint2HelperService.getContentLanguageLabelModels(),
            Descriptions: this.fiCheckpoint2HelperService.getContentLanguageLabelModels(),
            Checkpoints: [],
        };
    }

    getDiscardChangesDialogData(): IFiDialogOptions<null, null> {
        return {
            isTopCloseButtonShown: false,
            headerText: this.appTextService.get('common.discard_changes_header'),
            messageText: this.appTextService.get('common.leave_page_without_saving_warning_message'),
            yesButtonText: this.appTextService.get('common.discard_changes'),
            ...FiChecklistModuleDiscardChangesDialogData,
        };
    }

    getAvailableSections(): ISectionCardData[] {
        // note: in case if we need to hide some sections, we should do it here
        return checklistEditDefaultSections.map((section: ISectionCardData) => {
            const availableSection = Object.assign({}, section);
            availableSection.headerTextLabel = this.appTextService.get(section.headerTextLabel);
            availableSection.subheaderTextLabel = this.appTextService.get(section.subheaderTextLabel);
            return availableSection;
        });
    }

    getCheckpointsAmount(checkpointGroups: ICheckpointGroup[]): number {
        return checkpointGroups.map((checkpointGroup) => checkpointGroup.Checkpoints.length).reduce((a, b) => a + b, 0);
    }

    isTranslationCardVisible(): boolean {
        return this.fiChecklistStoreService.availableContentLanguages?.length > 1;
    }

    getCurrentContentLanguageLabelModel(contentLanguageLabelModels: IContentLanguageLabelModel[]): string {
        return contentLanguageLabelModels.find((contentLanguageLabelModel: IContentLanguageLabelModel) => {
            return contentLanguageLabelModel.ContentLanguageId === this.fiChecklistStoreService.selectedLanguageId;
        })?.Value;
    }

    getChecklistInstanceV2(): IChecklistV2 {
        return {
            Names: this.fiCheckpoint2HelperService.getContentLanguageLabelModels(),
            Descriptions: this.fiCheckpoint2HelperService.getContentLanguageLabelModels(),
            ComplianceThresholdActions: [],
            MediaBankFileReferences: [],
            CheckpointGroups: [this.getCheckpointGroupInstanceV2()],
            AccessRightFilterCollection: AccessRightFilterCollectionModel.getNewInstance(),
            IsShared: true,
        };
    }

    setSelectedLanguageByNames(names: IContentLanguageLabelModel[]) {
        const selectedLanguageId = this.fiContentLanguageSharedService.getContentLanguageIdForDisplayContent(names);
        this.fiChecklistStoreService.selectedLanguageId = selectedLanguageId;
    }

    isCommentMandatory(answerValue: number, ranges: ICheckpointRange[], conditions: ICheckpointCondition[]): boolean {
        return this.isMandatory(answerValue, CheckpointConditionTargetType.Comment, ranges, conditions);
    }

    isAttachmentMandatory(
        answerValue: number,
        ranges: ICheckpointRange[],
        conditions: ICheckpointCondition[],
    ): boolean {
        return this.isMandatory(answerValue, CheckpointConditionTargetType.Attachment, ranges, conditions);
    }

    private isMandatory(
        answerValue: number,
        targetType: CheckpointConditionTargetType,
        ranges: ICheckpointRange[],
        conditions: ICheckpointCondition[],
    ): boolean {
        // todo if here will be changes - synchronise with backend
        const checkpointStatus = this.fiCheckpoint2HelperService.getCheckpointStatus(answerValue, ranges);
        if (checkpointStatus === Checkpoint2StatusType.NotFilledOut) {
            return false;
        }
        let isMandatory = false;
        for (let idx = 0; idx < conditions.length && !isMandatory; idx++) {
            const condition = conditions[idx];
            if (condition.TargetType === targetType) {
                if (condition.Type === CheckpointConditionType.Always) {
                    isMandatory = true;
                } else if (condition.Type === CheckpointConditionType.IsOk) {
                    isMandatory = checkpointStatus === Checkpoint2StatusType.Ok;
                } else if (condition.Type === CheckpointConditionType.IsNotOk) {
                    isMandatory = checkpointStatus === Checkpoint2StatusType.NotOk;
                } else {
                    throw new Error(`Unknown condition type ${condition.Type}`);
                }
            }
        }
        return isMandatory;
    }

    readonly checklistPlaceholderLabelData: Readonly<IChecklistCheckpointPlaceholderLabelData> = {
        namePlaceholderTextId: 'checklist.checklist_name_placeholder', // change to Create new checklist
        namePlaceholderHintTextId: 'checklist.checklist_name_placeholder_hint',
        descriptionPlaceholderHintTextId: 'checklist.checklist_description_placeholder_hint',
    };

    readonly checklistTemplatePlaceholderLabelData: Readonly<IChecklistCheckpointPlaceholderLabelData> = {
        namePlaceholderTextId: 'checklist.template_name_placeholder',
        namePlaceholderHintTextId: 'checklist.template_name_placeholder_hint',
        descriptionPlaceholderHintTextId: 'checklist.template_description_placeholder_hint',
    };

    readonly checkpointPlaceholderLabelData: Readonly<IChecklistCheckpointPlaceholderLabelData> = {
        namePlaceholderTextId: 'checklist.checkpoint_name_placeholder',
        namePlaceholderHintTextId: 'checklist.checkpoint_name_placeholder_hint',
        descriptionPlaceholderHintTextId: 'checklist.checkpoint_description_placeholder_hint',
    };

    getAddNewCheckpointDialog() {
        const labelData = this.checkpointPlaceholderLabelData;
        return this.fiDialogsService.custom({
            contentComponentRef: FiChecklistCommonDataDialogComponent,
            contentComponentData: {
                item: this.fiCheckpoint2HelperService.getCheckpointInstanceV2(),
                headingLabel: this.appTextService.get('checklist.checkpoint_new'),
                descriptionLabel: this.appTextService.get('common.edit_later_info'),
                namePlaceholderTextId: labelData.namePlaceholderTextId,
                namePlaceholderHintTextId: labelData.namePlaceholderHintTextId,
                descriptionPlaceholderHintTextId: labelData.descriptionPlaceholderHintTextId,
                showAttachmentsArea: false,
            },
            hideHeader: true,
            hideDialogTopBorder: true,
            yesButtonText: this.appTextService.get('done'),
            preventUppercaseButtonLabel: true,
            config: {
                panelClass: ['fi-dialog-limit-content', 'fi-rounded-dialog-panel'],
                maxWidth: '850px',
                width: '80%',
                maxHeight: '650px',
                height: '80%',
                closeOnNavigation: false,
            },
        });
    }

    openAddChecklistDialog(isAuditMode?: boolean) {
        this.fiChecklist2TemplateService
            .getLightweightChecklistTemplates(isAuditMode)
            .then((lightWeightTemplates: INameIdObj[]) => {
                this.fiDialogsService
                    .custom({
                        contentComponentRef: FiChecklistAddChecklistDialogComponent,
                        contentComponentData: {
                            lightWeightChecklistTemplates: lightWeightTemplates,
                            auditMode: isAuditMode,
                        },
                        hideHeader: true,
                        hideDialogTopBorder: true,
                        yesButtonText: this.appTextService.get('done'),
                        preventUppercaseButtonLabel: true,
                        config: {
                            panelClass: [
                                'fi-dialog-limit-content',
                                'fi-rounded-dialog-panel',
                                'fi-rounded-dialog-full-round-buttons',
                            ],
                            maxWidth: '850px',
                            width: '80%',
                            maxHeight: '850px',
                            height: '90%',
                            autoFocus: false,
                            closeOnNavigation: false,
                        },
                    })
                    .onYesButtonClick((dialogResult: IAddChecklistDialogResult) => {
                        const rootState = FiStateNames.getChecklistV2RootState(isAuditMode);
                        if (
                            dialogResult.type === ChecklistCreationTypes.Manual ||
                            dialogResult.type === ChecklistCreationTypes.UploadFromExcel
                        ) {
                            this.fiStateService.go(rootState.Add._name, { checklist: dialogResult.checklist });
                        } else {
                            this.fiStateService.go(rootState.Template.Copy._name, { id: dialogResult.templateId });
                        }
                    })
                    .afterClosed(() => {
                        this.saveChangesWarningService.closeDialogOnTransition = false;
                    });
            });

        this.saveChangesWarningService.closeDialogOnTransition = true;
    }

    openAddChecklistTemplateDialog(isAuditMode?: boolean) {
        this.fiDialogsService
            .custom({
                contentComponentRef: FiChecklistAddChecklistTemplateDialogComponent,
                contentComponentData: {
                    auditMode: isAuditMode,
                },
                hideHeader: true,
                hideDialogTopBorder: true,
                yesButtonText: this.appTextService.get('done'),
                preventUppercaseButtonLabel: true,
                config: {
                    panelClass: [
                        'fi-dialog-limit-content',
                        'fi-rounded-dialog-panel',
                        'fi-rounded-dialog-full-round-buttons',
                    ],
                    maxWidth: '850px',
                    width: '80%',
                    maxHeight: '650px',
                    height: '80%',
                    autoFocus: !this.fiCustomerDataService.customerData.HasTemplateCustomer,
                    closeOnNavigation: false,
                },
            })
            .onYesButtonClick((dialogResult: IAddChecklistTemplateDialogResult) => {
                const rootState = FiStateNames.getChecklistV2RootState(isAuditMode);
                if (
                    dialogResult.type === ChecklistCreationTypes.Manual ||
                    dialogResult.type === ChecklistCreationTypes.UploadFromExcel
                ) {
                    this.fiStateService.go(rootState.Template.Add._name, { checklistTemplate: dialogResult.checklist });
                } else {
                    this.fiChecklist2TemplateService
                        .getChecklistTemplateFromSample(dialogResult.templateId, isAuditMode)
                        .then((checklistTemplate: IChecklistV2) => {
                            checklistTemplate.AccessRightFilterCollection =
                                AccessRightFilterCollectionModel.getNewInstance();
                            this.fiStateService.go(rootState.Template.Add._name, {
                                checklistTemplate: checklistTemplate,
                                isTemplateBased: true,
                            });
                        });
                }
            })
            .afterClosed(() => {
                this.saveChangesWarningService.closeDialogOnTransition = false;
            });

        this.saveChangesWarningService.closeDialogOnTransition = true;
    }

    /*openAddChecklistCommonDataDialog(args: IChecklistV2ModeArgs) {
        if (!args) {
            args = {};
        }
        const labelData: IChecklistCheckpointPlaceholderLabelData = !args.templateMode ?
            this.checklistPlaceholderLabelData : this.checklistTemplatePlaceholderLabelData;

        this.fiDialogsService.custom({
            contentComponentRef: FiChecklistCommonDataDialogComponent,
            contentComponentData: {
                item: this.getChecklistInstanceV2(),
                headingLabel: this.appTextService.get(!args.templateMode ? 'checklist.create_new_checklist' : 'checklist.create_new_template'),
                descriptionLabel: this.appTextService.get('common.edit_later_info'),
                namePlaceholderTextId: labelData.namePlaceholderTextId,
                namePlaceholderHintTextId: labelData.namePlaceholderHintTextId,
                descriptionPlaceholderHintTextId: labelData.descriptionPlaceholderHintTextId,
                showAttachmentsArea: true,
                templateMode: true,
            },
            hideHeader: true,
            hideDialogTopBorder: true,
            isNewButtonsUI: true,
            yesButtonText: this.appTextService.get('done'),
            preventUppercaseButtonLabel: true,
            config: {
                panelClass: 'fi-dialog-limit-content',
                maxWidth: '850px',
                width: '80%',
                maxHeight: '650px',
                height: '80%',
                closeOnNavigation: false
            }
        }).onYesButtonClick((newChecklist: IChecklistV2) => {
            const rootState = FiStateNames.getChecklistV2RootState(args.auditMode);
            if (!args.templateMode) {
                this.fiStateService.go(rootState.Add._name, {checklist: newChecklist})
            } else {
                this.fiStateService.go(rootState.Template.Add._name, {checklistTemplate: newChecklist})
            }
        }).afterClosed(() => {
            this.saveChangesWarningService.closeDialogOnTransition = false;
        });
        this.saveChangesWarningService.closeDialogOnTransition = true;
    }*/

    //#region confirmations
    getConfirmDeleteChecklistResponsesDialog(
        checklistName: string,
        scheduleName: string,
        answersCount: number,
    ): IFiDialogResult<boolean> {
        const dialogContentData: IDialogConfirmDeleteData = {
            step1: {
                message: this.appTextService.get('checklist.confirm_delete_checklist_responses_text_v2_step1', {
                    '{0}': fiTextToHtml(checklistName) + ' - ' + fiTextToHtml(scheduleName),
                    '{1}': answersCount,
                }),
                buttonText: this.appTextService.get('common.confirm'),
                confirmTextToInput: this.appTextService.get('common.delete').toUpperCase(),
                preventUppercaseButtonLabel: true,
            },
            step2: {
                message: 'checklist.confirm_delete_checklist_responses_text_v2_step2',
                buttonText: this.appTextService.get('common.confirm'),
                preventUppercaseButtonLabel: true,
            },
        };

        return this.getConfirmDeleteDialog(
            dialogContentData,
            this.appTextService.get('checklist.confirm_delete_responses_header'),
        );
    }

    getConfirmDeleteChecklistDialog(args: {
        item: IChecklistV2ViewerBase;
        checklistIdFn: () => number;
        auditMode: boolean;
    }): TResultPromise {
        const deleteChecklistDialogHeader = this.appTextService.get('checklist.delete_checklist');
        const item = args.item;
        const promise = new Promise<TResultPromiseData>((resolve, reject) => {
            if (item.ConnectedSchedulesCount > 0) {
                // fetch light weight schedules by checklist id
                const checklistId = args.checklistIdFn();

                this.fiChecklist2ScheduleService
                    .getChecklistSchedulesByChecklistId(checklistId, args.auditMode)
                    .then((schedules) => {
                        const scheduleNames = schedules.map((schedule) => fiTextToHtml(schedule.Name)).join(', ');

                        const messageText = this.appTextService.get('checklist.delete_linked_checklist_not_allowed', {
                            '{0}': item.ConnectedSchedulesCount,
                            '{1}': scheduleNames,
                        });
                        this.getDeleteChecklistNotAllowedDialog(deleteChecklistDialogHeader, messageText)
                            .onYesButtonClick(() => {
                                const rootState = FiStateNames.getChecklistV2RootState(args.auditMode);

                                this.fiStateService.go(
                                    rootState.List,
                                    {
                                        ids: [checklistId],
                                        filterType: SideListOptionType.SharedWithMe,
                                    },
                                    { reload: true },
                                );

                                resolve({ result: false });
                            })
                            .onNoButtonClick(() => resolve({ result: false }));
                    }, reject);
            } else {
                const dialogContentData: IDialogConfirmDeleteData = {
                    step1: {
                        message: 'checklist.confirm_delete_checklist_text_v2_step1',
                        buttonText: this.appTextService.get('common.confirm'),
                        confirmTextToInput: this.appTextService.get('common.delete').toUpperCase(),
                        preventUppercaseButtonLabel: true,
                    },
                    step2: {
                        message: 'checklist.confirm_delete_checklist_text_v2_step2',
                        buttonText: this.appTextService.get('common.confirm'),
                        preventUppercaseButtonLabel: true,
                    },
                };

                this.getConfirmDeleteDialog(dialogContentData, deleteChecklistDialogHeader)
                    .onYesButtonClick(() => resolve({ result: true }))
                    .onNoButtonClick(() => resolve({ result: false }));
            }
        });
        return promise;
    }

    private getConfirmDeleteDialog(
        dialogContentData: IDialogConfirmDeleteData,
        headerText: string,
    ): IFiDialogResult<boolean> {
        return this.fiDialogsService.custom({
            headerText,
            contentComponentRef: FiDialogConfirmDeleteComponent,
            contentComponentData: dialogContentData,
            colorClass: 'red',
            headerIconClass: 'far fa-circle-exclamation red',
            isBigSizeTopBorder: true,
            preventUppercaseButtonLabel: true,
            config: {
                panelClass: 'fi-rounded-dialog-panel',
                maxWidth: '560px',
            },
        });
    }

    private getDeleteChecklistNotAllowedDialog(headerText: string, messageText: string): IFiDialogResult<boolean> {
        return this.fiDialogsService.custom<void, boolean>({
            headerText,
            messageText,
            colorClass: 'red',
            isBigSizeTopBorder: true,
            headerIconClass: 'fa-light fa-trash red',
            preventUppercaseButtonLabel: true,
            yesButtonText: this.appTextService.get('checklist.show_connected_schedules'),
            config: {
                panelClass: 'fi-rounded-dialog-panel',
                maxWidth: '560px',
                autoFocus: false,
            },
        });
    }

    getConfirmTemplateDeleteDialog(): IFiDialogResult {
        return this.fiDialogsService.confirm({
            messageText: this.appTextService.get('checklist.delete_template_dialog_message'),
            headerText: this.appTextService.get('checklist.delete_template'),
            yesButtonText: this.appTextService.get('common.delete'),
            colorClass: 'red',
            isTopCloseButtonShown: true,
            headerIconClass: 'fa-light fa-trash red',
            preventUppercaseButtonLabel: true,
            config: {
                maxWidth: '640px',
                width: '90%',
                panelClass: 'fi-rounded-dialog-panel',
            },
        });
    }

    showTemplateDeletedSnackbar(item: { Id: number; Name: string }, auditMode: boolean) {
        const objectType = !auditMode ? ObjectTypes.Checklist2Template : ObjectTypes.Checklist2AuditTemplate;
        this.fiItemsDeleteHelperService.showDeletedObjectSimpleSnackBar(objectType, [item.Id], item.Name);
    }

    //#endregion

    //#region StartEndDateTimes

    getStartTimeText(
        item: IChecklistStartDueDateTimeModel,
        isActive: boolean,
        scheduleType: FiChecklistScheduleType,
        isScheduleList?: boolean,
    ): string {
        if (item.OperationalPeriodStatus === PublicationItemStatus.NotShared)
            return this.appTextService.get('common.not_operational');

        if (isScheduleList) {
            return this.fiFormatDateService.formatDate(item.OperationalStartDateStr, FiDateFormatOptions, {
                showToday: false,
                showUTC: false,
            });
        }

        if (this.isAuditOnDemandNotActive(isActive, scheduleType)) {
            if (this.isAuditSchedule(item.ScheduleType))
                return this.appTextService.get('checklist.schedule_interval_audit');
            if (this.isOnDemandSchedule(item.ScheduleType))
                return this.appTextService.get('checklist.schedule_interval_on_demand');
        }

        return this.fiFormatDateService.formatDate(item.StartDateTimeStr, FiDateTimeFormatOptions, {
            showToday: false,
            showUTC: true,
        });
    }

    getDueDateTimeText(
        item: IChecklistV2ForList | IChecklistScheduleForList,
        isChecklistScheduled: boolean,
        isScheduleList?: boolean,
    ): string {
        if (isChecklistScheduled) {
            if (item.OperationalPeriodStatus === PublicationItemStatus.NotShared) return '';

            if (isScheduleList) {
                if (!item.OperationalDueDateStr) {
                    return this.appTextService.get('common.continuously_operational');
                }
                return this.fiFormatDateService.formatDate(item.OperationalDueDateStr, FiDateFormatOptions, {
                    showToday: false,
                    showUTC: false,
                });
            }

            if (this.isAuditOnDemandNotActive(item.IsActive, item.ScheduleType)) {
                return this.appTextService.get('checklist.schedule_due_date_time_audit_on_demand', {
                    '{0}': this.fiChecklist2ScheduleFormatService.getAuditOnDemandEndIntervalLabel(
                        item.TimeIntervals[0].End,
                    ),
                });
            }

            if (!item.DueDateTime) {
                return this.appTextService.get('none');
            }

            return this.fiFormatDateService.formatDate(item.DueDateTimeStr, FiDateTimeFormatOptions, {
                showToday: false,
                showUTC: true,
            });
        }
        return '';
    }

    /*getDueDateTimeTextOld(item: IChecklistV2ForList): string {
        if (item.ScheduleId) {
            if (item.DueDateTime)
                return this.fiFormatDateService.formatDate(item.DueDateTimeStr, FiDateTimeFormatOptions,
                    {showToday: false, showUTC: true});
            return this.appTextService.get('none');
        }
        return '';
    }*/

    isOnDemandSchedule(scheduleType: FiChecklistScheduleType): boolean {
        return scheduleType === FiChecklistScheduleType.OnDemand;
    }

    isAuditSchedule(scheduleType: FiChecklistScheduleType): boolean {
        return scheduleType === FiChecklistScheduleType.Audit;
    }

    isOnAuditOrDemandChecklistSchedule(scheduleType: FiChecklistScheduleType): boolean {
        return (
            [FiChecklistScheduleType.Audit, FiChecklistScheduleType.OnDemand] as FiChecklistScheduleType[]
        ).includes(scheduleType);
    }

    //#endregion

    clearCheckpoint(checkpointAnswer: ICheckpoint2AnswerModel) {
        checkpointAnswer.Value = undefined;
        checkpointAnswer.MediaBankFileReferences = [];
        checkpointAnswer.Comments[0].Content = undefined;
    }

    isCommentEmpty(checkpointAnswer: ICheckpoint2AnswerModel) {
        return !checkpointAnswer.Comments[0]?.Content;
    }

    isAttachmentsEmpty(checkpointAnswer: ICheckpoint2AnswerModel) {
        return !checkpointAnswer.MediaBankFileReferences.length;
    }

    isEmptyAnswerWithCommentAttachments(checkpointAnswer: ICheckpoint2AnswerModel) {
        return (
            isNullOrUndefined(checkpointAnswer.Value) &&
            (!this.isCommentEmpty(checkpointAnswer) || !this.isAttachmentsEmpty(checkpointAnswer))
        );
    }

    isAuditOnDemandNotActive(isActive: boolean, scheduleType: FiChecklistScheduleType) {
        // https://chainformation.atlassian.net/browse/FI-6026
        // https://chainformation.atlassian.net/browse/FI-6114
        return (
            !isActive &&
            (scheduleType === FiChecklistScheduleType.Audit || scheduleType === FiChecklistScheduleType.OnDemand)
        );
    }

    isAuditOnDemandDueDateSoon(item: { ScheduleType: FiChecklistScheduleType; DueDateTime: string }): boolean {
        return (
            this.isTimeLessThenDay(item.DueDateTime) &&
            (item.ScheduleType === FiChecklistScheduleType.Audit ||
                item.ScheduleType === FiChecklistScheduleType.OnDemand)
        );
    }

    isTimeLessThenDay(time: string): boolean {
        if (!time) return false;
        const diff: number = new Date(time).getTime() - new Date().getTime();
        const diffH: number = diff / (1000 * 60 * 60);
        return diffH < 24;
    }

    confirmMarkNotApplicable(headerText: string, messageText: string): IFiDialogResult {
        return this.fiDialogsService.confirm({
            isTopCloseButtonShown: true,
            colorClass: 'orange',
            headerIconClass: 'orange far fa-circle-exclamation',
            headerText: headerText,
            messageText: messageText,
            yesButtonText: this.appTextService.get('common.confirm'),
            noButtonText: this.appTextService.get('common.cancel'),
            isNoButtonPrimaryColor: true,
            preventUppercaseButtonLabel: true,
            config: {
                panelClass: 'fi-rounded-dialog-panel',
                maxWidth: '640px',
                autoFocus: false,
            },
        });
    }

    showActionAddedSnackbar(taskName: string, checkpointName: string) {
        this.fiSnackbarService.showSnackbar({
            textMessage: this.appTextService.get('checklist_report.deviation_automated_task_saved', {
                '{task_name}': taskName,
                '{checkpoint_name}': checkpointName,
            }),
            buttonText: this.appTextService.get('common.dismiss'),
        });
    }

    showNotificationSavedSnackbar() {
        this.fiSnackbarService.showSnackbar({
            textMessage: this.appTextService.get('checklist_report.deviation_notification_saved'),
            buttonText: this.appTextService.get('common.dismiss'),
        });
    }

    isOperationalPeriodActive(checklistViewer: IChecklistV2ViewerBase): boolean {
        return checklistViewer.OperationalPeriodStatus === PublicationItemStatus.Active && checklistViewer.IsActive;
    }

    isChecklistAvailableLater(checklistViewer: IChecklistV2ViewerBase): boolean {
        return (
            !checklistViewer.IsActive &&
            !this.isAuditOnDemandNotActive(checklistViewer.IsActive, checklistViewer.ScheduleType) &&
            !!checklistViewer.StartDateTime
        );
    }

    isChecklistNotScheduled(checklistViewer: IChecklistV2ViewerBase): boolean {
        return !checklistViewer.ScheduleId;
    }

    getViewerStatus(checklistViewer: IChecklistV2ViewerBase): ChecklistViewerStatus {
        if (!checklistViewer.ChecklistToScheduleId) {
            return ChecklistViewerStatus.NotScheduled;
        }
        if (checklistViewer.OperationalPeriodStatus === PublicationItemStatus.NotShared) {
            return ChecklistViewerStatus.NotOperational;
        }
        if (checklistViewer.IsEnded) {
            // https://chainformation.atlassian.net/browse/FI-5619
            // checklist is expired if (it's type is not (OnDemand/Audit) and it has periods
            // in past and has no periods in future) or (it's type is (OnDemand/Audit) and
            // operational period is finished)
            // https://chainformation.atlassian.net/browse/FI-6025
            return ChecklistViewerStatus.Ended;
        }
        if (!checklistViewer.HasAccessToSchedule) {
            return ChecklistViewerStatus.ScheduleNotShared;
        }
        if (this.isChecklistAvailableLater(checklistViewer)) {
            // current date is not in TimeIntervals and there are some TimeInterval in future
            return ChecklistViewerStatus.AvailableLater;
        }
        if (checklistViewer.OperationalPeriodStatus === PublicationItemStatus.NotStarted) {
            return ChecklistViewerStatus.OperationalNotStarted;
        }
        if (!checklistViewer.ScheduleHasAccessToBusinessUnit) {
            return ChecklistViewerStatus.ScheduleNotSharedToBusinessUnit;
        }
        if (checklistViewer.IsSetNotApplicable) {
            return ChecklistViewerStatus.NotApplicable;
        }

        if (this.isOperationalPeriodActive(checklistViewer)) {
            return ChecklistViewerStatus.Active;
        }
        if (checklistViewer.ScheduleType === FiChecklistScheduleType.OnDemand) {
            return ChecklistViewerStatus.OnDemandReadyToStart;
        }

        return ChecklistViewerStatus.Unknown;
    }

    isAuditNotStarted(businessUnitId: number): boolean {
        return this.auditNotStartedUnitId === businessUnitId;
    }

    canChangeCheckpointNotApplicable(viewerDisabled: boolean, auditMode: boolean) {
        return !viewerDisabled || !auditMode;
    }

    getChecklistDiscardChangesDialogData(itemId: number, objectType: ObjectTypes): IFiDialogOptions<null, null> {
        if (!!itemId) return {};

        let objectTypeNameLabel: string = '';
        switch (objectType) {
            case ObjectTypes.ChecklistSchedule:
                objectTypeNameLabel = 'checklist.discard_schedule_warning';
                break;
            case ObjectTypes.Checklist2Template:
                objectTypeNameLabel = 'checklist.discard_template_warning';
                break;
            case ObjectTypes.CheckList:
            case ObjectTypes.Checklist2:
                objectTypeNameLabel = 'checklist.discard_checklist_warning';
                break;
        }

        return {
            headerText: this.appTextService.get('warning.exit_without_saving_question'),
            messageText: this.appTextService.get(objectTypeNameLabel),
            yesButtonText: this.appTextService.get('warning.exit_without_saving'),
            isTopCloseButtonShown: false,
            config: {
                panelClass: ['fi-rounded-dialog-panel', 'fi-dialog-no-button-primary-red'],
                autoFocus: false,
            },
        };
    }

    getChecklistViewState(item: IChecklistV2ForList, auditMode: boolean): string {
        if (!item.ChecklistToScheduleId) {
            return FiStateNames.getChecklistV2RootState(auditMode).ViewByChecklist;
        }
        return FiStateNames.getChecklistV2RootState(auditMode).View;
    }

    getChecklistViewStateParams(item: IChecklistV2ForList, auditMode: boolean): RawParams {
        if (!item.ChecklistToScheduleId) {
            return {
                id: item.Id,
            };
        }
        return {
            id: item.ChecklistToScheduleId,
            businessUnitId: item.BusinessUnitId,
        };
    }

    onChecklistListItemClick(item: IChecklistV2ForList, auditMode: boolean) {
        const viewState = this.getChecklistViewState(item, auditMode);
        const params = this.getChecklistViewStateParams(item, auditMode);
        this.fiStateService.registerReturnToCurrentState(viewState);
        this.fiStateService.go(viewState, params);
    }

    navigateToChecklistCheckpointSectionsEditor(
        templateId: number,
        checklistId: number,
        accessRightFilterCollection: IAccessRightFilterCollection,
    ) {
        const isTemplateBased: boolean = this.fiStateService.getCurrentStateParams().isTemplateBased;
        const templateMode: boolean = this.fiStateService.getCurrentStateParams().templateMode;

        this.fiStateService.go(this.getCurrentCheckpointSectionsEditorStateName(checklistId), {
            id: isTemplateBased && !templateMode ? templateId : checklistId,
            accessRightFilterCollection,
        });
    }

    navigateToEditCheckpoint(
        checkpointGroupIndex: number,
        checkpointIndex: number,
        checklistId: number,
        accessRightFilterCollection: IAccessRightFilterCollection,
    ) {
        this.fiStateService.go(this.getCurrentEditCheckpointStateName(checklistId), {
            checkpointGroupIndex,
            checkpointIndex,
            accessRightFilterCollection,
        });
    }

    private getCurrentCheckpointSectionsEditorStateName(checklistId: number): string {
        const auditMode: boolean = this.fiStateService.getCurrentStateParams().isAudit;
        const isTemplateBased: boolean = this.fiStateService.getCurrentStateParams().isTemplateBased;
        const templateMode: boolean = this.fiStateService.getCurrentStateParams().templateMode;
        const rootState = FiStateNames.getChecklistV2RootState(auditMode);

        if (isTemplateBased && !templateMode) {
            return rootState.Template.Copy.AddCheckpointSections._name;
        }
        if (templateMode) {
            if (checklistId) {
                return rootState.Template.Edit.EditCheckpointSections._name;
            }

            return rootState.Template.Add.AddCheckpointSections._name;
        }

        if (checklistId) {
            return rootState.Edit.EditCheckpointSections._name;
        }

        return rootState.Add.AddCheckpointSections._name;
    }

    private getCurrentEditCheckpointStateName(checklistId: number): string {
        const auditMode: boolean = this.fiStateService.getCurrentStateParams().isAudit;
        const isTemplateBased: boolean = this.fiStateService.getCurrentStateParams().isTemplateBased;
        const templateMode: boolean = this.fiStateService.getCurrentStateParams().templateMode;
        const rootState = FiStateNames.getChecklistV2RootState(auditMode);

        if (!checklistId) {
            if (isTemplateBased) return rootState.Template.Copy.AddCheckpointSections.EditCheckpoint;

            if (templateMode) return rootState.Template.Add.AddCheckpointSections.EditCheckpoint;

            return rootState.Add.AddCheckpointSections.EditCheckpoint;
        }

        if (templateMode) return rootState.Template.Edit.EditCheckpointSections.EditCheckpoint;

        return rootState.Edit.EditCheckpointSections.EditCheckpoint;
    }

    mergeCheckpointsWithAIResponse(
        checkpointGroups: ICheckpointGroup[],
        aiChecklist2: IAIChecklist2Model,
        currentContentLanguageId: FiApplicationLanguageType,
    ) {
        if (aiChecklist2.CheckpointGroups.length === 1) {
            // aiChecklist2 has one group, all checkpoints will be added to first checkpoint group

            aiChecklist2.CheckpointGroups[0].Checkpoints.forEach((aiCheckpoint) => {
                const newCheckpoint = this.createCheckpointFroAI(aiCheckpoint, currentContentLanguageId);
                checkpointGroups[0].Checkpoints.push(newCheckpoint);
            });
        } else {
            // aiChecklist2 has multiple group, add them to current groups

            aiChecklist2.CheckpointGroups.forEach((aiCheckpointGroup) => {
                const newGroupCheckpointGroup = this.getCheckpointGroupInstanceV2();
                newGroupCheckpointGroup.Names.filter(
                    (item) => item.ContentLanguageId == currentContentLanguageId,
                ).forEach((item) => (item.Value = aiCheckpointGroup.Name));
                newGroupCheckpointGroup.Descriptions.filter(
                    (item) => item.ContentLanguageId == currentContentLanguageId,
                ).forEach((item) => (item.Value = aiCheckpointGroup.Description));

                aiCheckpointGroup.Checkpoints.forEach((aiCheckpoint) => {
                    const newCheckpoint = this.createCheckpointFroAI(aiCheckpoint, currentContentLanguageId);
                    newGroupCheckpointGroup.Checkpoints.push(newCheckpoint);
                });

                checkpointGroups.push(newGroupCheckpointGroup);
            });
        }
    }

    private createCheckpointFroAI(aiCheckpoint: IAICheckpoint2Model, currentContentLanguageId: number) {
        const newCheckpoint = this.fiCheckpoint2HelperService.getCheckpointInstanceV2();
        newCheckpoint.Names.filter((item) => item.ContentLanguageId == currentContentLanguageId).forEach(
            (item) => (item.Value = aiCheckpoint.Name),
        );
        newCheckpoint.Descriptions.filter((item) => item.ContentLanguageId == currentContentLanguageId).forEach(
            (item) => (item.Value = aiCheckpoint.Description),
        );
        return newCheckpoint;
    }


}

interface IChecklistStartDueDateTimeModel {
    StartDateTime: string; // ISO 8601 datetime
    DueDateTime: string; // ISO 8601 datetime
    StartDateTimeStr: string;
    DueDateTimeStr: string;
    ScheduleType: FiChecklistScheduleType;
    IsActive: boolean; // determine if user can answer on checklist
    OperationalPeriodStatus: PublicationItemStatus;
    OperationalStartDateStr; // ISO 8601 datetime
    OperationalDueDateStr; // ISO 8601 datetime
}

export const FiChecklistModuleDiscardChangesDialogData: IFiDialogOptions<null, null> = {
    config: {
        panelClass: [
            'fi-rounded-dialog-panel',
            'fi-rounded-dialog-full-round-buttons',
            'fi-dialog-no-button-primary-red',
        ],
        autoFocus: false,
    },
};
